import { Loader2, Download, Activity, Sparkles } from "lucide-react";
import { useReport, exportCsv, fmtWhen, RCard, Tile } from "./shared";
import { homeLabel } from "@/lib/home";
import { V } from "@/lib/vocab";

const CHANNELS = [
  ["sms", "Texts", "#E5484D"],
  ["voice", "Voice calls", "#8B5CF6"],
  ["whatsapp", "WhatsApp", "#22C55E"],
  ["portal", "Portal", "#0EA5E9"],
];

export const UsageReport = ({ tenantId, periodKey, customStart, customEnd, reloadKey }) => {
  const { data, loading } = useReport("usage", tenantId, periodKey, customStart, customEnd, reloadKey);

  if (loading) return <div className="flex items-center gap-2 text-ink-500 py-24 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Building report…</div>;
  if (!data) return null;
  const { tiles, channels, items } = data;
  const chTotal = Object.values(channels || {}).reduce((a, b) => a + b, 0);

  const downloadCsv = () => exportCsv(
    "member-usage.csv",
    ["Name", "Phone", "Unit", "Address", "Texts", "Calls", "Total", "Last activity", "First-time this period"],
    items.map((i) => [i.name, i.phone, i.unit, i.address, i.texts, i.calls, i.total, fmtWhen(i.last_at), i.first_time ? "Yes" : "No"]),
  );

  return (
    <div data-testid="usage-report" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile testid="usage-tile-adoption" label="Adoption" value={`${tiles.adoption_pct}%`}
          sub={`${tiles.ever_active} of ${tiles.roster_total} ${V.lcs("person")} have used it`} />
        <Tile testid="usage-tile-active" label="Active this period" value={tiles.active_period} sub="texted or called" />
        <Tile testid="usage-tile-firsttime" label="First-time users" value={tiles.first_time} sub="started this period" />
        <Tile testid="usage-tile-texts" label="Texts received" value={tiles.texts} sub="inbound messages" />
        <Tile testid="usage-tile-calls" label="Voice calls" value={tiles.calls} sub="concierge phone calls" />
      </div>

      {chTotal > 0 && (
        <RCard className="p-5" data-testid="usage-channel-split">
          <div className="text-[11px] font-bold tracking-[0.14em] text-ink-400 uppercase mb-3">How {V.lcs("person")} reach you</div>
          <div className="h-3 w-full rounded-full overflow-hidden flex bg-sand-100">
            {CHANNELS.map(([k, , color]) => channels[k] > 0 && (
              <div key={k} style={{ width: `${(channels[k] / chTotal) * 100}%`, background: color }} title={`${k}: ${channels[k]}`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-xs text-ink-600">
            {CHANNELS.map(([k, label, color]) => channels[k] > 0 && (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: color }} />
                <b>{label}</b> {channels[k]} · {Math.round((channels[k] / chTotal) * 100)}%
              </span>
            ))}
          </div>
        </RCard>
      )}

      <RCard>
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.14em] text-ink-400 uppercase">
            <Activity className="h-3.5 w-3.5 text-redline-500" strokeWidth={2} /> Most active {V.lcs("person")}
          </div>
          <button data-testid="usage-export-csv" onClick={downloadCsv} disabled={!items.length}
            className="rounded-full border border-sand-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-ink-600 hover:border-ink transition-colors flex items-center gap-1.5 disabled:opacity-40">
            <Download className="h-3.5 w-3.5" strokeWidth={2} /> CSV
          </button>
        </div>
        {!items.length ? (
          <div className="px-5 pb-8 pt-2 text-sm text-ink-500">No {V.lc("person")} activity in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider font-bold text-muted-foreground border-b border-sand-200">
                  <th className="px-5 py-2.5">{V.one("person")}</th>
                  <th className="px-3 py-2.5">Property</th>
                  <th className="px-3 py-2.5 text-right">Texts</th>
                  <th className="px-3 py-2.5 text-right">Calls</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                  <th className="px-5 py-2.5 text-right">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.person_id} data-testid={`usage-row-${i.person_id}`} className="border-b border-sand-100 last:border-0">
                    <td className="px-5 py-3">
                      <div className="font-semibold text-ink flex items-center gap-1.5">
                        {i.name}
                        {i.first_time && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-redline-50 border border-redline-200 text-redline-600 px-2 py-0.5 text-[10px] font-bold">
                            <Sparkles className="h-2.5 w-2.5" strokeWidth={2} /> New
                          </span>
                        )}
                      </div>
                      {i.phone && <div className="text-xs text-ink-500">{i.phone}</div>}
                    </td>
                    <td className="px-3 py-3 text-ink-600">{homeLabel(i) || "—"}</td>
                    <td className="px-3 py-3 text-right font-semibold text-ink">{i.texts}</td>
                    <td className="px-3 py-3 text-right font-semibold text-ink">{i.calls}</td>
                    <td className="px-3 py-3 text-right font-heading font-extrabold text-ink">{i.total}</td>
                    <td className="px-5 py-3 text-right text-ink-500 whitespace-nowrap">{fmtWhen(i.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </RCard>
    </div>
  );
};
